import {
    InMessageTopic,
    SanitisesPayload,
} from 'embedded/api/in/InMessageTopic';
import { AnyAction, Store } from 'redux';
import { LoggingService } from '@instinet-analytics/core-lib';
import {
    AuthenticateMessagePayload,
    authenticatePayloadSchema,
} from 'embedded/api/in/messages/AuthenticateMessagePayload';
import { loginJwtAction } from 'login/store/thunks';
import { AppMode } from 'app/store/general/AppMode';
import { ThunkDispatch } from 'store/store';
import { v4 as uuid } from 'uuid';

export class AuthenticateMessage
    extends InMessageTopic<AuthenticateMessagePayload>
    implements SanitisesPayload<AuthenticateMessagePayload>
{
    private previous:
        | { timestamp: number; message: AuthenticateMessagePayload }
        | undefined;

    public constructor() {
        super(authenticatePayloadSchema, {
            supportedAppModes: [
                AppMode.EMBEDDED_SINGLE_WIDGET,
                AppMode.EMBEDDED_DASHBOARD,
            ],
            mustBeAuthenticated: false,
        });
    }

    public handle(
        uid: string,
        payload: AuthenticateMessagePayload,
        store: Store,
        loggingService: LoggingService
    ): void {
        if (this.isDuplicate(payload)) {
            loggingService.info(
                'EmbeddedApi.AuthenticateMessage',
                'Duplicate authenticate message ignored',
                payload
            );
            return;
        }
        this.previous = { timestamp: new Date().getTime(), message: payload };

        const { token, application, machineId, applicationVersion } = payload;
        const dispatch = store.dispatch as ThunkDispatch<AnyAction>;
        const correlationid = uuid();
        dispatch(
            loginJwtAction({
                token,
                application,
                applicationVersion,
                machineId,
                correlationid,
            })
        );
    }

    public sanitizePayload(payload?: AuthenticateMessagePayload) {
        return {
            application: payload?.application,
            tokenLength: (payload?.token ?? '').length,
        };
    }

    private isDuplicate(payload: AuthenticateMessagePayload) {
        if (!this.previous) {
            return false;
        }

        if (new Date().getTime() - this.previous.timestamp > 2000) {
            // Only ignore if received within 2 secs
            return;
        }

        return (
            this.previous.message.token === payload.token &&
            this.previous.message.application === payload.application &&
            this.previous.message.applicationVersion ===
                payload.applicationVersion
        );
    }
}
